import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';

// App-wide light / dark theme (STRY0039116).
//
// The preference is one of 'light' | 'dark' | 'system' and lives in
// localStorage under STORAGE_KEY ('system' is stored as "no key"). The resolved
// theme is written to <html data-theme="light|dark">; every colour in the
// portal is a token in bz-tokens.css that switches on that attribute, so a
// change applies live with no reload. index.html carries a tiny inline script
// that sets the same attribute before the bundle loads (no white flash); this
// provider re-derives it on mount and keeps it in sync.

export const STORAGE_KEY = 'bzThemePreference';
export const DARK_MEDIA_QUERY = '(prefers-color-scheme: dark)';
export const PREFERENCES = ['system', 'light', 'dark'];

const isExplicit = value => value === 'light' || value === 'dark';

// 'light' | 'dark' | 'system'. Anything unreadable or unknown is 'system'.
export const readStoredPreference = (storage = safeLocalStorage()) => {
  try {
    const value = storage && storage.getItem(STORAGE_KEY);
    return isExplicit(value) ? value : 'system';
  } catch (e) {
    return 'system';
  }
};

export const writeStoredPreference = (preference, storage = safeLocalStorage()) => {
  try {
    if (!storage) return;
    if (isExplicit(preference)) storage.setItem(STORAGE_KEY, preference);
    else storage.removeItem(STORAGE_KEY);
  } catch (e) {
    // Private mode / blocked storage: the choice still applies for this session.
  }
};

// The OS/browser setting. False when matchMedia is unavailable (default light).
export const systemPrefersDark = (win = typeof window !== 'undefined' ? window : undefined) => {
  try {
    return !!(win && typeof win.matchMedia === 'function' && win.matchMedia(DARK_MEDIA_QUERY).matches);
  } catch (e) {
    return false;
  }
};

// Pure: what the page should show for a preference and an OS answer.
export const resolveTheme = (preference, prefersDark) => {
  if (isExplicit(preference)) return preference;
  return prefersDark ? 'dark' : 'light';
};

export const applyThemeAttribute = (theme, doc = typeof document !== 'undefined' ? document : undefined) => {
  if (!doc || !doc.documentElement) return;
  doc.documentElement.setAttribute('data-theme', theme);
};

function safeLocalStorage() {
  try {
    return typeof window !== 'undefined' ? window.localStorage : undefined;
  } catch (e) {
    return undefined;
  }
}

const noop = () => {};

export const ThemeContext = createContext({
  preference: 'system',
  theme: 'light',
  isDark: false,
  setPreference: noop,
  toggle: noop
});

export const ThemeProvider = ({ children }) => {
  const [preference, setPreferenceState] = useState(() => readStoredPreference());
  const [prefersDark, setPrefersDark] = useState(() => systemPrefersDark());

  // Follow the OS while the preference is 'system'.
  useEffect(() => {
    if (preference !== 'system' || typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
      return undefined;
    }
    const mql = window.matchMedia(DARK_MEDIA_QUERY);
    const onChange = e => setPrefersDark(!!e.matches);
    setPrefersDark(!!mql.matches);
    if (typeof mql.addEventListener === 'function') {
      mql.addEventListener('change', onChange);
      return () => mql.removeEventListener('change', onChange);
    }
    if (typeof mql.addListener === 'function') {
      mql.addListener(onChange);
      return () => mql.removeListener(onChange);
    }
    return undefined;
  }, [preference]);

  const theme = resolveTheme(preference, prefersDark);

  useEffect(() => {
    applyThemeAttribute(theme);
  }, [theme]);

  const setPreference = useCallback(next => {
    const value = isExplicit(next) ? next : 'system';
    writeStoredPreference(value);
    setPreferenceState(value);
  }, []);

  const toggle = useCallback(() => {
    setPreference(theme === 'dark' ? 'light' : 'dark');
  }, [setPreference, theme]);

  const value = useMemo(() => ({ preference, theme, isDark: theme === 'dark', setPreference, toggle }), [
    preference,
    theme,
    setPreference,
    toggle
  ]);

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
};

ThemeProvider.propTypes = { children: PropTypes.node };

export const useTheme = () => useContext(ThemeContext);

export default ThemeContext;
